import type { CsActionReason } from "@/api/customerSupportUsers.service";

// Text for the coded values the CS panel receives. Codes arrive exactly as they are stored
// (`senior_executive`, `website.registered_user`) because the write side is an enum with no label
// table to join, so anything without its own vocabulary is derived from the code itself rather
// than kept in a dictionary here that would silently fall back to the raw code for every value
// added to the enum later.

/**
 * `senior_executive` → "Senior executive".
 *
 * A value that already reads as prose is returned untouched: sentence-casing one would turn an
 * acronym ("CEO") into "Ceo".
 */
export function humaniseCode(value: string): string {
  const trimmed = value.trim();
  if (!/[_-]/.test(trimmed) && trimmed !== trimmed.toLowerCase()) return trimmed;

  const words = trimmed.replace(/[_\s-]+/g, " ").trim();
  if (words === "") return trimmed;
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * Access tiers arrive namespaced by the surface that grants them (`website.registered_user`).
 * Every tier reachable from this panel is a website role, so the namespace tells an agent
 * nothing the page does not already say — the profile keeps the full raw value on the rendered
 * value's title for anyone reconciling the panel against the database.
 */
export function humaniseAccessTier(value: string): string {
  return humaniseCode(value.slice(value.lastIndexOf(".") + 1));
}

/**
 * Labels for the reason taxonomy, shared by the action modals (which offer them) and the profile
 * (which reads back the one that was filed), so a deletion filed as "GDPR erasure" is not
 * reported back as `gdpr_erasure`. Typed against the reason union so adding a reason to any
 * action's enum forces a label here.
 */
export const CS_REASON_LABEL: Record<CsActionReason, string> = {
  user_request: "User request",
  cannot_sign_in: "Cannot sign in",
  gdpr_erasure: "GDPR erasure",
  bounced_email: "Bounced email",
  duplicate: "Duplicate",
  scheduled_in_error: "Scheduled in error",
  other: "Other",
};

/**
 * A filed reason is a plain string on the wire (a self-service deletion is not written by the
 * staff endpoints), so an unmapped code still has to read as text rather than as a code.
 */
export function csReasonLabel(reason: string): string {
  return CS_REASON_LABEL[reason as CsActionReason] ?? humaniseCode(reason);
}
